import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getProjects, getCustomers, createDesignDoc, getJobStatus, getProjectCodeDocs } from '../services/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, DraftingCompass, PencilRuler, Upload, UserRound, ShieldCheck, CircleHelp,
  FileCode2, Sparkles, FileJson,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import JobProgressTracker from '../components/upload/JobProgressTracker';
import FileUploadTab from '../components/upload/FileUploadTab';
import { uploadingStatus } from '../utils/slimZip';
import ImportDocumentPanel from '../components/ImportDocumentPanel';

/**
 * Design Builder — the one door for every design-phase document.
 *
 * It opens on a single question: is this a NEW design (something that does not
 * exist yet — written by the SOW engine as a priced Statement of Work) or a CHANGE
 * REQUEST to an integration that already runs?
 *
 * A change request is written from the customer's own code, so it needs a project
 * whose code has been documented in Code Docs first (code zip, repository or B1iF
 * package). The writer then reads that code AND its documentation, checks the ticket
 * against it, and only then drafts. Without Code Docs the page sends the consultant
 * to Code Docs instead of writing a document on assumptions.
 *
 * "Include price" picks the house form: on, a Change Request (hours + $ ex GST);
 * off, a High-Level Design Proposal (hours only — the price is agreed elsewhere).
 *
 * Progress is polled off the shared job-status endpoint (~3s); on completion it
 * routes to the design draft at /design-builder/:docId.
 *
 * "Import document" files a finished document (a DocHub draft .json) under a
 * project instead of writing one, then opens its normal draft page.
 */

// The choice the page opens on. Each card explains what the document is for so
// the decision is made on substance rather than on the label alone.
const DOC_TYPES = {
  new_design: {
    label: 'New Design',
    icon: DraftingCompass,
    blurb: 'Something that does not exist yet — a new integration or system. Writes the Statement of Work: solution specification, testing approach and a priced estimate.',
  },
  change_request: {
    label: 'Change Request',
    icon: PencilRuler,
    blurb: 'A change to an integration that already runs. Reads its code and Code Docs, checks the ticket against them, then writes what exists today, what changes and the estimate.',
  },
};

const DesignBuilderPage = () => {
  const { canUpload, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedProject = searchParams.get('project');

  // The document type is deliberately unset — the page asks before anything else —
  // unless we arrived from Code Docs with a project, which is always a change request.
  const [docType, setDocType] = useState(preselectedProject ? 'change_request' : '');
  // On = Change Request (hours + $ ex GST); off = High-Level Design Proposal (hours only).
  const [includePrice, setIncludePrice] = useState(true);

  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedProject, setSelectedProject] = useState(preselectedProject || '');
  const [title, setTitle] = useState('');
  const [brief, setBrief] = useState('');
  const [pastedEmail, setPastedEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerContact, setCustomerContact] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  // Import a finished document instead of writing one.
  const [importing, setImporting] = useState(false);

  // The selected project's Code Docs: null = not checked yet, [] = never documented.
  const [codeDocs, setCodeDocs] = useState(null);

  // Job tracking
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobStartTime, setJobStartTime] = useState(null);
  const [jobElapsedMin, setJobElapsedMin] = useState(0);
  const createdDocId = useRef(null);
  const pollInterval = useRef(null);
  const elapsedTimer = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [customersRes, projectsRes] = await Promise.all([
        getCustomers(),
        getProjects(),
      ]);
      setCustomers(customersRes.data);
      setProjects(projectsRes.data);

      if (preselectedProject) {
        const proj = projectsRes.data.find(p => p.id === preselectedProject);
        if (proj) {
          setSelectedCustomer(proj.customer_id);
          setSelectedProject(proj.id);
        }
      }
    } catch (error) {
      toast.error('Failed to load customers and projects');
    } finally {
      setLoading(false);
    }
  }, [preselectedProject]);

  useEffect(() => {
    if (!canUpload) {
      navigate('/documentation');
      return;
    }
    fetchData();
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
      if (elapsedTimer.current) clearInterval(elapsedTimer.current);
    };
  }, [canUpload, navigate, fetchData]);

  // Check whether the chosen project's code has been documented.
  useEffect(() => {
    if (!selectedProject) { setCodeDocs(null); return; }
    let cancelled = false;
    setCodeDocs(null);
    getProjectCodeDocs(selectedProject)
      .then(res => { if (!cancelled) setCodeDocs(res.data || []); })
      .catch(() => { if (!cancelled) setCodeDocs([]); });
    return () => { cancelled = true; };
  }, [selectedProject]);

  const filteredProjects = selectedCustomer
    ? projects.filter(p => p.customer_id === selectedCustomer)
    : projects;

  const goToDraft = useCallback((docId) => {
    const target = docId || createdDocId.current;
    if (target) navigate(`/design-builder/${target}`);
  }, [navigate]);

  const startPolling = useCallback((jId) => {
    if (pollInterval.current) clearInterval(pollInterval.current);
    if (elapsedTimer.current) clearInterval(elapsedTimer.current);

    const startTime = Date.now();
    setJobStartTime(startTime);
    setJobElapsedMin(0);

    elapsedTimer.current = setInterval(() => {
      setJobElapsedMin(Math.floor((Date.now() - startTime) / 60000));
    }, 30000);

    pollInterval.current = setInterval(async () => {
      // Poll deadline: a backend restart mid-job can strand it forever. Stop
      // after ~20 min and surface a retry prompt instead of an infinite spinner.
      // Longer than the SOW deadline: the code is read and checked before drafting.
      if (Date.now() - startTime > 20 * 60 * 1000) {
        clearInterval(pollInterval.current);
        clearInterval(elapsedTimer.current);
        pollInterval.current = null;
        elapsedTimer.current = null;
        setJobStatus((s) => (s ? { ...s, status: 'failed', error: 'This page has stopped waiting after 20 minutes. The job may still finish — look for the document under Documentation before starting again.' } : s));
        toast.error('This page has stopped waiting after 20 minutes — the job may still finish.');
        return;
      }
      try {
        const res = await getJobStatus(jId);
        setJobStatus(res.data);

        if (res.data.status === 'complete') {
          clearInterval(pollInterval.current);
          clearInterval(elapsedTimer.current);
          pollInterval.current = null;
          elapsedTimer.current = null;
          toast.success('Change request ready!');
          goToDraft(res.data.documentation_id);
        } else if (res.data.status === 'failed') {
          clearInterval(pollInterval.current);
          clearInterval(elapsedTimer.current);
          pollInterval.current = null;
          elapsedTimer.current = null;
          toast.error(res.data.error || 'Document generation failed');
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, 3000);
  }, [goToDraft]); // eslint-disable-line react-hooks/exhaustive-deps -- getJobStatus is a stable module import

  const hasCodeDocs = Array.isArray(codeDocs) && codeDocs.length > 0;

  const chooseDocType = (value) => {
    // A new design is the SOW engine's job — hand over to its intake page.
    if (value === 'new_design') { navigate('/sow-builder'); return; }
    setDocType(value);
  };

  const handleSubmit = async () => {
    if (docType !== 'change_request') {
      toast.error('Please choose Change Request to continue');
      return;
    }
    if (!title.trim()) {
      toast.error('Please enter a title for the document');
      return;
    }
    if (!hasCodeDocs) {
      toast.error('Document the code in Code Docs first, then create the change request');
      return;
    }

    const formData = new FormData();
    formData.append('doc_type', includePrice ? 'change_request' : 'design');
    formData.append('title', title);
    files.forEach((f) => formData.append('files', f));
    if (brief.trim()) formData.append('brief', brief);
    if (pastedEmail.trim()) formData.append('pasted_email', pastedEmail);
    formData.append('project_id', selectedProject);
    if (customerName.trim()) formData.append('customer_name', customerName);
    if (customerContact.trim()) formData.append('customer_contact', customerContact);

    try {
      setJobStatus({ status: 'uploading', progress: 0, message: 'Uploading inputs...' });
      // Percent, MB sent and time left go onto the job card while it is still uploading
      const response = await createDesignDoc(formData, (evt) => {
        setJobStatus((s) => (s?.status === 'uploading' ? uploadingStatus(evt) : s));
      });
      const jId = response.data.job_id;
      createdDocId.current = response.data.doc_id || null;
      setJobId(jId);
      setJobStatus({
        status: 'processing',
        progress: 5,
        message: 'Reading the code and its documentation, then checking the ticket against them...',
      });
      startPolling(jId);
    } catch (error) {
      setJobStatus(null);
      setJobId(null);
      createdDocId.current = null;
      toast.error(error.response?.data?.detail || 'Failed to start the document');
    }
  };

  const resetJob = () => {
    if (pollInterval.current) clearInterval(pollInterval.current);
    if (elapsedTimer.current) clearInterval(elapsedTimer.current);
    createdDocId.current = null;
    setJobId(null);
    setJobStatus(null);
    setFiles([]);
    setTitle('');
    setBrief('');
    setPastedEmail('');
    setCustomerName('');
    setCustomerContact('');
  };

  const isProcessing = jobStatus && !['complete', 'failed'].includes(jobStatus.status);
  const canGenerate = docType === 'change_request' && !!title.trim() && hasCodeDocs;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8 max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="design-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']" data-testid="design-page-title">
              Design Builder
            </h1>
            <p className="text-slate-500 text-sm">
              A new design, or a change request written from the customer's documented code
            </p>
          </div>
          {!importing && !isProcessing && (
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => setImporting(true)}
              data-testid="design-import-doc-btn"
            >
              <FileJson className="h-4 w-4 mr-1.5" />
              Import document
            </Button>
          )}
        </div>

        {importing && (
          <ImportDocumentPanel
            customers={customers}
            projects={projects}
            initialCustomer={selectedCustomer}
            initialProject={selectedProject}
            onCancel={() => setImporting(false)}
          />
        )}

        {/* Job Status Card */}
        <JobProgressTracker
          jobStatus={importing ? null : jobStatus}
          jobElapsedMin={jobElapsedMin}
          isProcessing={isProcessing}
          onViewDocs={() => goToDraft(jobStatus?.documentation_id)}
          onReset={resetJob}
          workingLabel="Reading the code and its documentation, checking the ticket against them, then drafting..."
          normalTime="8 to 12 minutes"
        />

        {/* Main form - hidden while processing or after completion */}
        {!importing && !isProcessing && jobStatus?.status !== 'complete' && (
          <div className="space-y-6">
            {/* Document type — the first question the page asks */}
            <Card className={docType ? 'border-slate-200' : 'border-[#2B8FD8]/50 ring-1 ring-[#2B8FD8]/10'}>
              <CardHeader>
                <CardTitle className="text-base font-['Plus_Jakarta_Sans']">
                  What are we writing? *
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(DOC_TYPES).map(([value, cfg]) => {
                    const Icon = cfg.icon;
                    const active = docType === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => chooseDocType(value)}
                        data-testid={`design-doc-type-${value}`}
                        className={`text-left rounded-xl border-2 p-4 transition-colors ${
                          active
                            ? 'border-[#2B8FD8] bg-blue-50/60'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <span className="flex items-center gap-2 mb-1.5">
                          <Icon className={`h-4 w-4 ${active ? 'text-[#2B8FD8]' : 'text-slate-400'}`} />
                          <span className={`text-sm font-semibold ${active ? 'text-[#2B8FD8]' : 'text-slate-700'}`}>
                            {cfg.label}
                          </span>
                        </span>
                        <span className="block text-xs leading-relaxed text-slate-500">{cfg.blurb}</span>
                      </button>
                    );
                  })}
                </div>
                {docType === 'change_request' && (
                  <label className="flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2.5 cursor-pointer" data-testid="design-include-price">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={includePrice}
                      onChange={(e) => setIncludePrice(e.target.checked)}
                    />
                    <span className="text-xs leading-relaxed text-slate-600">
                      <span className="font-semibold text-slate-700">Include the price (ex GST)</span> — the customer approves
                      the change and its cost from this document. Untick when the price is already agreed on the ticket:
                      you get a High-Level Design Proposal with hours only.
                    </span>
                  </label>
                )}
                {!docType && (
                  <p className="text-xs text-slate-500 flex items-center gap-1.5">
                    <CircleHelp className="h-3.5 w-3.5 text-slate-400" />
                    Pick one to continue — the two documents are written differently.
                  </p>
                )}
              </CardContent>
            </Card>

            {docType === 'change_request' && (
              <>
                {/* Customer / project — the code the change request is written from */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-base font-['Plus_Jakarta_Sans']">
                      Customer &amp; project *
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Customer</Label>
                        <Select
                          value={selectedCustomer}
                          onValueChange={(v) => {
                            setSelectedCustomer(v);
                            setSelectedProject('');
                            const c = customers.find(c => c.id === v);
                            if (c && !customerName.trim()) setCustomerName(c.name);
                          }}
                        >
                          <SelectTrigger data-testid="design-customer-select">
                            <SelectValue placeholder="Select customer" />
                          </SelectTrigger>
                          <SelectContent>
                            {customers.map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Project</Label>
                        <Select value={selectedProject} onValueChange={setSelectedProject}>
                          <SelectTrigger data-testid="design-project-select">
                            <SelectValue placeholder="Select project" />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredProjects.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Has this project's code been documented? */}
                    {selectedProject && codeDocs === null && (
                      <p className="text-xs text-slate-500 flex items-center gap-1.5">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking the project's Code Docs…
                      </p>
                    )}
                    {selectedProject && hasCodeDocs && (
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-3 text-xs leading-relaxed text-emerald-800 flex items-start gap-2" data-testid="design-code-docs-ready">
                        <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
                        <span>
                          The change request will read this project's code
                          {codeDocs[0]?.files_analyzed ? ` (${codeDocs[0].files_analyzed} files)` : ''} and its
                          documentation: {codeDocs.map(d => d.title).join(' and ')}
                          {codeDocs[0]?.created_at ? `, documented ${new Date(codeDocs[0].created_at).toLocaleDateString()}` : ''}.
                          If the code has changed since, document it again in Code Docs first.
                        </span>
                      </div>
                    )}
                    {selectedProject && Array.isArray(codeDocs) && !hasCodeDocs && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 text-xs leading-relaxed text-amber-800" data-testid="design-code-docs-missing">
                        <p className="flex items-start gap-2">
                          <CircleHelp className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                          <span>
                            <span className="font-semibold">Document the code first.</span> A change request is written from
                            the customer's own code. In Code Docs, add the code (.zip), repository or B1iF package for this
                            project and document it — then come back and create the change request.
                          </span>
                        </p>
                        <Button
                          size="sm"
                          className="mt-3 bg-[#2B8FD8] hover:bg-[#2075b3]"
                          onClick={() => navigate(`/code-docs?project=${selectedProject}`)}
                          data-testid="design-open-code-docs-btn"
                        >
                          <FileCode2 className="h-4 w-4 mr-1.5" /> Open Code Docs for this project
                        </Button>
                      </div>
                    )}
                    {!selectedProject && (
                      <p className="text-xs text-slate-500">
                        Pick the project whose code this change applies to.
                      </p>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Customer legal name (for the document header)</Label>
                        <Input
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          placeholder="e.g., EHI Australia Pty Ltd — read from the ticket if blank"
                          data-testid="design-customer-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Customer contact</Label>
                        <Input
                          value={customerContact}
                          onChange={(e) => setCustomerContact(e.target.value)}
                          placeholder="e.g., Jane Doe, CFO — read from the ticket if blank"
                          data-testid="design-customer-contact-input"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Core inputs */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-base font-['Plus_Jakarta_Sans']">What is changing?</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g., WooCommerce Ship To address update for existing customers"
                        data-testid="design-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Brief</Label>
                      <textarea
                        value={brief}
                        onChange={(e) => setBrief(e.target.value)}
                        placeholder="Describe the change, or paste chat notes. Anything already agreed with the customer — the option chosen, hours quoted, what is out of scope — the document will honour."
                        rows={7}
                        className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2B8FD8] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
                        data-testid="design-brief-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Author</Label>
                      <div
                        className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600"
                        data-testid="design-author-readonly"
                      >
                        <UserRound className="h-4 w-4 text-slate-400" />
                        <span className="font-medium text-slate-700">{user?.name || 'You'}</span>
                        <span className="text-xs text-slate-400">— the document is prepared in your name</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Ticket files */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    Halo ticket / email files <span className="text-slate-400 font-normal">(the whole thread, if you have it)</span>
                  </h3>
                  <FileUploadTab
                    multiple
                    file={files}
                    setFile={setFiles}
                    onUpload={handleSubmit}
                    disabled={!canGenerate}
                    accept=".pdf,.eml,.msg,.docx,.xlsx,.csv,.txt,.md"
                    hint="Halo tickets, emails, Word / Excel / CSV exports — PDF, EML, MSG, DOCX, XLSX, CSV, TXT, MD. Code goes into Code Docs, not here."
                  />
                </div>

                {/* Pasted email */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-base font-['Plus_Jakarta_Sans']">Paste raw email</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label>Email content <span className="text-slate-400 font-normal">(optional)</span></Label>
                      <textarea
                        value={pastedEmail}
                        onChange={(e) => setPastedEmail(e.target.value)}
                        placeholder="Paste the raw email thread here if you don't have a file to upload..."
                        rows={6}
                        className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2B8FD8] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
                        data-testid="design-pasted-email-input"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Primary submit */}
                <Button
                  className="w-full bg-[#2B8FD8] hover:bg-[#2075b3] h-12 text-base"
                  onClick={handleSubmit}
                  disabled={!canGenerate}
                  data-testid="design-generate-btn"
                >
                  <Sparkles className="h-5 w-5 mr-2" />
                  {includePrice ? 'Generate Change Request' : 'Generate Design Proposal'}
                </Button>
                {!canGenerate && (
                  <p className="text-xs text-center text-slate-500 -mt-2">
                    {!hasCodeDocs ? 'Pick a project whose code is documented in Code Docs.' : 'Enter a title to continue.'}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DesignBuilderPage;
